import React, { useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Paper,
  Grid,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { calculateAffordabilityBaseline } from '../../services/api';

const STATES = [
  { code: 'NV', name: 'Nevada', hasTax: false },
  { code: 'CA', name: 'California', hasTax: true },
];

const PAY_INTERVALS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
];

function BaselineIncomeConfig({
  state,
  setState,
  incomes,
  setIncomes,
  baselineResult,
  setBaselineResult,
}) {
  // Calculate baseline whenever inputs change
  const calculateBaseline = useCallback(async () => {
    if (!incomes.some(i => i.gross_annual > 0)) {
      setBaselineResult(null);
      return;
    }

    try {
      // Use bi-weekly as default for baseline calculation display
      // Actual frequency per income is used when linking templates
      const response = await calculateAffordabilityBaseline({
        state,
        pay_interval: 'bi-weekly',
        incomes: incomes.map(inc => ({
          label: inc.label,
          gross_annual: inc.gross_annual,
          type: inc.type,
          frequency: inc.frequency || 'bi-weekly'
        }))
      });
      setBaselineResult(response.data);
    } catch (err) {
      console.error('Baseline calculation error:', err);
    }
  }, [state, incomes, setBaselineResult]);

  useEffect(() => {
    const timer = setTimeout(() => {
      calculateBaseline();
    }, 500); // Debounce

    return () => clearTimeout(timer);
  }, [calculateBaseline]);

  const handleAddIncome = () => {
    const newId = Math.max(...incomes.map(i => i.id), 0) + 1;
    setIncomes([...incomes, { id: newId, label: '', gross_annual: 0, type: 'w2', frequency: 'bi-weekly' }]);
  };

  const handleRemoveIncome = (id) => {
    if (incomes.length > 1) {
      setIncomes(incomes.filter(i => i.id !== id));
    }
  };

  const handleIncomeChange = (id, field, value) => {
    setIncomes(incomes.map(inc => {
      if (inc.id === id) {
        return { ...inc, [field]: field === 'gross_annual' ? Number(value) || 0 : value };
      }
      return inc;
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Your Current Setup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter your gross annual income. We'll calculate your take-home based on state taxes.
      </Typography>

      <FormControl fullWidth sx={{ mb: 4 }}>
        <InputLabel>Current State</InputLabel>
        <Select
          value={state}
          label="Current State"
          onChange={(e) => setState(e.target.value)}
        >
          {STATES.map(s => (
            <MenuItem key={s.code} value={s.code}>
              {s.name} {s.hasTax ? '(has state tax)' : '(no state tax)'}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1">Income Sources</Typography>
          <IconButton onClick={handleAddIncome} color="primary" size="small">
            <AddIcon />
          </IconButton>
        </Box>

        {incomes.map((income, index) => (
          <Paper key={income.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Label"
                  value={income.label}
                  onChange={(e) => handleIncomeChange(income.id, 'label', e.target.value)}
                  placeholder={`Income ${index + 1}`}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Gross Annual"
                  type="number"
                  value={income.gross_annual || ''}
                  onChange={(e) => handleIncomeChange(income.id, 'gross_annual', e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={income.type}
                    label="Type"
                    onChange={(e) => handleIncomeChange(income.id, 'type', e.target.value)}
                  >
                    <MenuItem value="w2">W-2</MenuItem>
                    <MenuItem value="c2c">C2C/1099</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={income.frequency || 'bi-weekly'}
                    label="Frequency"
                    onChange={(e) => handleIncomeChange(income.id, 'frequency', e.target.value)}
                  >
                    {PAY_INTERVALS.map(p => (
                      <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <IconButton
                  onClick={() => handleRemoveIncome(income.id)}
                  disabled={incomes.length === 1}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Box>

      {baselineResult && (
        <Paper sx={{ p: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
          <Typography variant="h6" gutterBottom>
            Current Take-Home
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <Typography variant="body2">Gross Annual</Typography>
              <Typography variant="h6">{formatCurrency(baselineResult.gross_annual)}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="body2">Net Annual</Typography>
              <Typography variant="h6">{formatCurrency(baselineResult.net_annual)}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="body2">Net Monthly</Typography>
              <Typography variant="h6">{formatCurrency(baselineResult.net_monthly)}</Typography>
            </Grid>
          </Grid>

          {baselineResult.withholdings && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.3)' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>Annual Withholdings:</Typography>
              <Typography variant="body2">
                Federal: {formatCurrency(baselineResult.withholdings.federal)} |
                State: {formatCurrency(baselineResult.withholdings.state)} |
                SS: {formatCurrency(baselineResult.withholdings.ss)} |
                Medicare: {formatCurrency(baselineResult.withholdings.medicare)}
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default BaselineIncomeConfig;
